import React, { useEffect, useState } from 'react';
import ReactInputMask from 'react-input-mask';

import { parseTime, TIME_MASK } from '../../../utils/tournament/dateTimeInput';

interface ITimeInputProps {
  /** HH:MM em 24h. */
  value: string;
  /** Só é chamado com um horário completo e válido. */
  onChange?: (value: string) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  'aria-label'?: string;
  /** Input estilizado da tela (ex.: StyledInput do cronograma). */
  inputComponent?: React.ElementType;
}

/**
 * Hora em HH:MM (24h), independente do idioma do navegador — o `type="time"`
 * nativo mostra AM/PM num sistema em inglês. Enquanto o horário está
 * incompleto, nada muda; ao sair do campo com um valor inválido, ele volta ao
 * último horário válido.
 */
const TimeInput: React.FC<ITimeInputProps> = ({
  value,
  onChange,
  disabled = false,
  style,
  'aria-label': ariaLabel,
  inputComponent: InputComponent = 'input',
}) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setDraft(next);
    const parsed = parseTime(next);
    if (parsed && parsed !== value) onChange?.(parsed);
  };

  return (
    <ReactInputMask
      mask={TIME_MASK}
      value={draft}
      disabled={disabled}
      onChange={handleChange}
      onBlur={() => {
        if (!parseTime(draft)) setDraft(value);
      }}
    >
      {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
        <InputComponent
          {...inputProps}
          // O react-input-mask tira disabled das props do filho: repassar o
          // mesmo valor aqui é o que de fato desabilita o input.
          disabled={disabled}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="HH:MM"
          aria-label={ariaLabel}
          style={style}
        />
      )}
    </ReactInputMask>
  );
};

export default TimeInput;
